/**
 * Realtime server-event reaction layer - split out of useRealtimeVoice.js
 * (2026-08-22) to keep that file from growing further, per the standing
 * <300-line rule. Owns tool-call dispatch (executeTool, handleFunctionCall)
 * and the full data-channel message handler. useRealtimeVoice.js owns
 * connection setup (mint, mic, peer connection, session.update/greeting,
 * SDP negotiation) and calls dc.onmessage = onMessage.
 *
 * Trust-boundary invariants this file maintains (Room 202 forensics,
 * 2026-08-22):
 *   - assistantSpeakingRef reflects actual audio PLAYBACK, via
 *     output_audio_buffer.started/stopped/cleared, not response.done
 *     (generation-complete only - was blind while Aria's audio was still
 *     physically playing).
 *   - Every real transcript is saved independently via postTurn() the
 *     moment it's known - no scalar "pending" ref that a later turn could
 *     silently overwrite before the first was ever persisted.
 *   - end_call/end_conversation only actually tear down the connection
 *     when the underlying tool call succeeded (ok:true) - a suspect turn
 *     gets a spoken confirmation instead of silently ending the session.
 */
import { API } from "./api";
import { executeOperationsTool } from "./realtimeOperationsTools";
import { executeDeviceTool } from "./realtimeDeviceTools";
import { executeSelfCodeTool } from "./realtimeSelfCodeTools";
import { executeDisplayTool } from "./realtimeDisplayTools";
import { executeCareTool, ringLiveLineOnSilence } from "./realtimeCareControl";
import { executeCallTool } from "./realtimeCallTool";
import { createFarewellWatch } from "./farewellWatch";
import { createClaimGuard } from "./claimGuard";
import { createDeltaLead } from "./deltaLead";
import { createClaimInterrupter, claimInterrupterMode, verifiedConfirmationEvent } from "./claimInterrupter";
import { logRealtimeEvent, logTranscriptionUsage, sanitizeRealtimeUsage, transcriptionConfidence, LOW_CONFIDENCE_THRESHOLD } from "./realtimeDiagnostics";
import { TRANSCRIPTION_MODEL } from "./realtimeSessionUpdate";
import { reenableAutoResponse, createGreetingResponseGate } from "./realtimeAutoResponseGate";
import { createHangupScheduler } from "./endCallHangup";
import { classifyEndIntent, isSoftEnding, turnMayEnd } from "./endIntent";
import { createEndController } from "./endController";
import { createSentenceTail } from "./sentenceTail";
import { createTurnGroundingTracker } from "./realtimeTurnGrounding";
import { typedTurnFromMessage, TYPED_TURN_CLASS, onTypedEcho, onTypedResponseCreated, onTypedResponseDone } from "./realtimeTypedTurn";

// Dispatches a model-emitted function call to whichever tool module claims
// it (operations/device/display, in that order), falling back to "not
// wired yet" if none do. Designed so a missing room or device fails
// gracefully ("I couldn't reach the AC, I'll let the nurse know") instead
// of throwing the whole session.
// 2026-08-23 (Room 304 forensic report): overlap timing alone falsely
// flagged genuine barge-in as suspect - 7 of ~11 overlap-flagged turns in
// that session were coherent, on-topic resident statements, not echo. A
// resident is allowed to interrupt Aria on purpose. Overlap is now ONE
// piece of evidence, combined with two more that are actually available
// (segment length, textual resemblance to what Aria just said) plus a
// short-term streak of tiny overlapping fragments - not a fabricated
// confidence score, three explicit, traceable checks. Ambient-silence
// phantoms (no overlap at all) are deliberately untouched here - separate,
// still-open issue per the incident report.
function classifyUserTurn({ overlapped, text, lastAssistantText, tinyStreak }) {
  if (!overlapped) return { suspect: false, reason: "no_overlap" };
  const trimmed = (text || "").trim();
  const wordCount = trimmed.split(/\s+/).filter(Boolean).length;
  const isTiny = wordCount <= 2 || !/[a-zA-Z]/.test(trimmed);
  if (!isTiny) {
    // Coherent multi-word statement while Aria was talking - real,
    // deliberate barge-in. Overlap alone isn't reason to distrust it.
    return { suspect: false, reason: "coherent_barge_in" };
  }
  const resemblesAssistant = !!lastAssistantText &&
    lastAssistantText.toLowerCase().includes(trimmed.toLowerCase().replace(/[.,!?]/g, ""));
  if (resemblesAssistant || tinyStreak >= 2) {
    return { suspect: true, reason: resemblesAssistant ? "echo_like" : "repeated_tiny_fragments" };
  }
  return { suspect: true, reason: "uncertain_fragment" };
}

// Context handed to the operations tools. It must carry last_user_text, or
// request_staff_help cannot store the resident's own words (RQ-033).
export function opsToolContext(ctx) {
  return { room: ctx?.room, residentId: ctx?.resident_id, sessionId: ctx?.session_id, turnSuspect: ctx?.turn_suspect, turnSuspectReason: ctx?.turn_suspect_reason, last_user_text: ctx?.last_user_text };
}

async function executeTool({ name, args, ctx }) {
  try {
    const opsCtx = opsToolContext(ctx);
    for (const dispatch of [() => executeOperationsTool({ name, args, ctx: opsCtx }), () => executeDeviceTool({ name, args, ctx }), () => executeDisplayTool({ name, args }), () => executeCareTool({ name, args, ctx }), () => executeCallTool({ name, args, ctx: opsCtx }), () => executeSelfCodeTool({ name, args, ctx })]) {
      const result = await dispatch();
      if (result) return result;
    }
    return { ok: false, message: `tool ${name} is not wired yet.` };
  } catch (e) {
    return { ok: false, message: `tool error: ${e?.message || "unknown"}.` };
  }
}

export function createRealtimeHandlers({
  myGen, startGenRef, sessionIdRef, ctxRef, caos, send, stop, onEndCall,
  turnSuspectRef, assistantSpeakingRef, restingRef, greetingCreateResponseOffRef,
  setStatus, setResting, setTranscript, setError, onFirstSpeechStarted,
  startAwaitingAnswerTimer, onSpeechEvent, typedTurnRef, micVoice,
}) {
  // Speech-lifecycle -> the companion INACTIVITY state machine
  // (realtimeInactivityTimer.js via realtimeConnection.js). ACTIVE SPEECH =
  // NO TIMER; the window runs only while BOTH sides are silent. kind is one
  // of "resident_start" | "resident_stop" | "aria_start" | "aria_stop".
  const speech = (kind) => { try { onSpeechEvent?.(kind); } catch { /* ignore */ } };
  // Closure-local, not refs - createRealtimeHandlers runs once per
  // connection and these handlers persist for its lifetime, same as any
  // ref would, without threading more state through useRealtimeVoice.js.
  let lastAssistantText = "";
  let tinyFragmentStreak = 0;
  // 2026-08-24 (Room 404 forensics, Issue #22): diagnostic-only timing -
  // VAD segment duration and time since Aria's audio genuinely stopped.
  // Logged only, no classification behavior change.
  let lastSpeechStartedAt = null;
  let lastSpeechSegmentMs = null;
  let lastSpeechStoppedAt = null;
  let lastPlaybackStoppedAt = null;
  const greetingGate = createGreetingResponseGate({ send, caos, greetingCreateResponseOffRef });
  let endCallKind = "resident_end_call";
  let endVia = "end_call_tool";   // what armed the hangup: "transcript" (local match) or "end_call_tool" (the model)
  // RQ-055 ending state machine (endController.js): after endNow() every later event is ignored.
  const endCtl = createEndController({ send, stop, onEndCall, log: (type, d) => logRealtimeEvent(sessionIdRef.current, type, d),
    goodbyeLine: caos.context?.goodbye_line || undefined,
    // Closing: no server auto-reply and no barge-in cancel of the goodbye (endController.js).
    // turn_detection null = no server VAD at all while closing (rt_d3ahe1bf: with create/interrupt false the
    // goodbye was still cleared 0.3 s in, and the server's answer to the update was not on record).
    quietServer: () => send({ type: "session.update", session: { type: "realtime", audio: { input: { turn_detection: null } } } }) });
  const hangup = createHangupScheduler({ // waits for the goodbye audio (endCallHangup.js)
    onClose: () => endCtl.finish(endCallKind, endVia),
  });
  const turnGrounding = createTurnGroundingTracker(); // see realtimeTurnGrounding.js
  const sentenceTail = createSentenceTail();   // a VAD-split "...that's all." is not a goodbye (sentenceTail.js)
  const farewellWatch = createFarewellWatch((type, d) => logRealtimeEvent(sessionIdRef.current, type, d));
  const deltaLead = createDeltaLead((type, d) => logRealtimeEvent(sessionIdRef.current, type, d));
  const claimGuard = createClaimGuard((type, d) => logRealtimeEvent(sessionIdRef.current, type, d));
  const claimMode = claimInterrupterMode(); // RQ-050: "off" by default; see claimInterrupter.js
  const claimInterrupter = createClaimInterrupter({ send, mode: claimMode, log: (type, d) => logRealtimeEvent(sessionIdRef.current, type, d) });
  const typedItemsSeen = new Set(); // typed-turn echoes, once each (realtimeTypedTurn.js)

  // Saves one turn immediately, independently - no pairing, no waiting on
  // the other side of the exchange. See RealtimeTurnIngest's docstring
  // (routes/realtime_memory_ingest.py) for the real data-loss bug this
  // replaced: a scalar "pending" ref could be overwritten by a second
  // user segment before the first was ever saved.
  const postTurn = (role, text, itemId, trusted) => {
    if (!text) return;
    const rid = ctxRef.current?.resident_id;
    const ownerId = ctxRef.current?.owner_user_id;
    if (rid) {
      fetch(`${API}/memory/realtime-turn`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          resident_id: rid, session_id: sessionIdRef.current, role, text, trusted, item_id: itemId || null,
          room: ctxRef.current?.room || null, kiosk_id: ctxRef.current?.kiosk_id || null,
        }),
      }).catch(() => {});
    } else if (ownerId) {
      // Aria's own (operator) sessions - same immediate per-turn pattern.
      // Owner-only endpoint (RQ-025): send the signed-in owner's token.
      const token = localStorage.getItem("caos_token");
      fetch(`${API}/aria/conversation-turn`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ owner_user_id: ownerId, session_id: sessionIdRef.current, role, content: text }),
      }).catch(() => {});
    }
  };

  // Helper: dispatch a tool call coming from the model
  // Several tool calls in one turn (e.g. seven schedule lookups): ask for ONE
  // reply after the last finishes - one per call collided ("active response").
  let toolsInFlight = 0;
  let providerSession = {};   // the provider's own session id/model (session.created/updated), for paid API receipts
  let deferredReply = null;
  const handleFunctionCall = async (fn) => {
    if (endCtl.ending) return;
    let parsed = {};
    try { parsed = fn.arguments ? JSON.parse(fn.arguments) : {}; } catch {}
    logRealtimeEvent(sessionIdRef.current, "tool_call", { meta: { name: fn.name, args: parsed } });
    endCtl.noteCallOpened(fn.call_id);
    claimInterrupter.onToolStart(fn.name);
    toolsInFlight += 1;
    let cls, result;
    try {
      cls = await turnGrounding.waitForGroundedTurn();
      result = await executeTool({ name: fn.name, args: parsed, ctx: { ...ctxRef.current, turn_suspect: cls.suspect, turn_suspect_reason: cls.reason, last_user_text: cls.text || "" } });
    } finally {
      toolsInFlight -= 1;
    }
    const isEnd = fn.name === "end_call" || fn.name === "end_conversation";
    // The model heard an ending the transcript lost (RQ-055): its second
    // end request in a row is granted instead of asking "are you sure?" again.
    if (isEnd && !result?.ok) result = endCtl.corroborate(cls.reason, cls.text) || result;
    if (endCtl.ending) return;
    logRealtimeEvent(sessionIdRef.current, "tool_result", { meta: { name: fn.name, result } });
    claimGuard.onToolResult(fn.name, result);
    claimInterrupter.onToolResult(fn.name, result);
    if ((fn.name === "end_call" || fn.name === "end_conversation") && result?.ok) farewellWatch.noteEndCallOk();
    if (myGen !== startGenRef.current) return;
    // Tell the model what happened. The output goes onto the conversation
    // as a `function_call_output` item, then we ask the model to respond.
    endCtl.noteCallAnswered(fn.call_id);
    send({
      type: "conversation.item.create",
      item: {
        type: "function_call_output",
        call_id: fn.call_id,
        output: JSON.stringify(result),
      },
    });
    // 2026-09-23 (Room 214, rt_wpngzbuw): a REFUSED mark_resting ("Just to
    // make sure - would you like me to go quiet?") used to take this branch
    // too, silencing Aria with no response.create - she never asked the
    // question and sat mute. Only a granted rest rests; a refusal falls
    // through to the normal branch so she speaks the confirmation.
    if (fn.name === "mark_resting" && result.ok) {
      // 2026-08-29 (real bug, confirmed live): setResting(true) alone never
      // stopped the SERVER auto-responding to every VAD-detected utterance -
      // resting was a UI dim only, so she kept replying in a tight loop.
      // Same create_response:false mechanism already used for the greeting
      // window actually stops her talking - re-enabled on real wake below.
      setResting(true);
      restingRef.current = true;
      if (caos.turn_detection) {
        send({
          type: "session.update",
          session: { type: "realtime", audio: { input: { turn_detection: { ...caos.turn_detection, create_response: false } } } },
        });
      }
    } else if (fn.name === "end_call" || fn.name === "end_conversation") {
      // 2026-08-22 (real bug, confirmed live): a phantom echo turn ("and")
      // reached this branch and hung up on the resident mid-session.
      // end_call/end_conversation are NOT emergency actions, so only tear
      // down when the tool actually succeeded - realtimeDeviceTools.js now
      // refuses (ok:false, asks for confirmation instead) when the
      // triggering turn was flagged suspect.
      send({ type: "response.create" });
      if (result.ok) {
        endCallKind = fn.name === "end_call" ? "resident_end_call" : "resident_end_conversation";
        endVia = "end_call_tool";
        hangup.arm();
        endCtl.markEnding(assistantSpeakingRef.current);
      }
    } else {
      // Ask the model to speak its short confirmation, drawing on the tool result.
      // RQ-050 (mode "on"): carry the verified message instead of letting it improvise.
      const reply = (claimMode === "on" && verifiedConfirmationEvent(fn.name, result)) || { type: "response.create" };
      if (toolsInFlight > 0) deferredReply = deferredReply || reply;   // the last one to finish replies
      else { deferredReply = null; send(reply); }
      // Level 1 live-line routing question (2026-09-06): the resident was
      // just asked "someone in the room now, or talk to me?" - the
      // directive treats silence/no answer as "now." Armed here rather
      // than in realtimeCareControl.js since only this file has the
      // speech-detection hook to clear it if they DO answer.
      if (fn.name === "request_live_staff" && result.awaiting_answer && startAwaitingAnswerTimer) {
        startAwaitingAnswerTimer(result.ring_timeout_sec, () => {
          ringLiveLineOnSilence(ctxRef.current?.alert_id);
        });
      }
    }
  };

  const onMessage = (ev) => {
    if (myGen !== startGenRef.current || endCtl.ended) return;
    let msg;
    try { msg = JSON.parse(ev.data); } catch { return; }
    // While closing, keep the server's answer to quietServer on record (it is otherwise filtered out below).
    if (endCtl.ending && (msg.type === "session.updated" || msg.type === "error")) {
      logRealtimeEvent(sessionIdRef.current, msg.type === "error" ? "closing_realtime_error" : "closing_session_updated", {
        text: msg.error?.message || "", meta: { turn_detection: msg.session?.audio?.input?.turn_detection ?? null } });
    }
    // ENDING: only the goodbye's own audio/response events matter (they time the hang-up); nothing else may restart the call.
    if (endCtl.ending && !/^(output_audio_buffer\.|response\.(created|done|output_audio_transcript\.done))/.test(msg.type || "")) return;

    if (msg.type === "input_audio_buffer.speech_started") {
      setStatus("listening");
      // Wake from rest: re-enable server auto-response (mirrors the
      // greeting window's own re-enable below) - genuinely over, not just visual.
      if (restingRef.current) {
        restingRef.current = false;
        reenableAutoResponse({ send, caos });
      }
      setResting(false);   // resident spoke again → wake from rest
      // Raw overlap signal - did this segment start while Aria's audio was
      // still playing? One piece of evidence, not the verdict - see
      // classifyUserTurn() above, applied once the transcript resolves.
      turnSuspectRef.current = assistantSpeakingRef.current;
      lastSpeechStartedAt = Date.now();
      sentenceTail.onSpeechStarted(msg.item_id);
      claimGuard.onUserTurn();
      claimInterrupter.onUserTurn();
      speech("resident_start");   // resident speaking -> cancel any pending inactivity window
      logRealtimeEvent(sessionIdRef.current, "speech_started", { assistantSpeaking: assistantSpeakingRef.current });
      // Level 1 invite-silence timer (useRealtimeVoice.js) needs to know
      // the FIRST time the resident actually speaks this session, to clear
      // its "did they respond to the invite" window. Guarded by the
      // caller (fires once, then no-ops) rather than here.
      try { onFirstSpeechStarted?.(); } catch {}
    }
    if (msg.type === "input_audio_buffer.speech_stopped") {
      setStatus("live");
      lastSpeechSegmentMs = lastSpeechStartedAt ? Date.now() - lastSpeechStartedAt : null;
      lastSpeechStoppedAt = Date.now();
      sentenceTail.onSpeechStopped();
      turnGrounding.onSpeechStopped();
      speech("resident_stop");    // resident silent -> arm the window IF Aria is also silent
      logRealtimeEvent(sessionIdRef.current, "speech_stopped", { assistantSpeaking: assistantSpeakingRef.current });
    }
    if (msg.type === "response.audio.delta") {
      setStatus("speaking");
    }
    // 2026-08-22 (real bug, confirmed live via Room 202 forensics):
    // assistantSpeakingRef used to flip false on response.done, which is
    // generation-complete, not audio-playback-complete. A multi-second
    // reply keeps physically playing through the speaker well after
    // response.done fires, so the trust boundary was blind during that
    // window - three of five phantom turns in one session started 1-1.4s
    // after response.done, almost certainly while Aria's own audio was
    // still audible. output_audio_buffer.started/stopped/cleared are real
    // server events sent specifically for WebRTC/SIP connections (verified
    // against an OpenAI team member's direct confirmation, not guessed -
    // they do NOT exist on plain WebSocket, which is why this was missed
    // before) and track actual playback lifecycle, not generation.
    if (msg.type === "output_audio_buffer.started") {
      deltaLead.onAudioStarted(msg);
      endCtl.noteAudioStarted();
      sentenceTail.onAriaAudio();
      assistantSpeakingRef.current = true;
      greetingGate.onAudioStarted();
      hangup.onAudioStarted();
      speech("aria_start");       // Aria speaking -> cancel any pending inactivity window
      // Logged standalone (2026-08-24) - was only folded into other events.
      logRealtimeEvent(sessionIdRef.current, "output_audio_buffer_started", {});
    }
    if (msg.type === "output_audio_buffer.stopped" || msg.type === "output_audio_buffer.cleared") {
      // .cleared covers a genuine interruption cutting playback short -
      // either way, Aria's audio is no longer physically playing.
      assistantSpeakingRef.current = false;
      lastPlaybackStoppedAt = Date.now();
      speech("aria_stop");        // Aria silent -> arm the window IF the resident is also silent
      logRealtimeEvent(sessionIdRef.current, "output_audio_buffer_stopped", { meta: { cleared: msg.type === "output_audio_buffer.cleared" } });
      // Re-enable normal auto-response once the forced greeting's own audio
      // has ACTUALLY finished playing - see realtimeAutoResponseGate.js.
      greetingGate.onAudioStopped();
      hangup.onAudioStopped();
    }
    if (msg.type === "response.done" && typedTurnRef?.current) onTypedResponseDone(typedTurnRef.current, send, msg.response);
    if (msg.type === "session.created" || msg.type === "session.updated") providerSession = { id: msg.session?.id || providerSession.id, model: msg.session?.model || providerSession.model };
    if (msg.type === "response.done") {
      setStatus("live");
      logRealtimeEvent(sessionIdRef.current, "response_done", {
        responseId: msg.response?.id,
        meta: {
          usage: typeof sanitizeRealtimeUsage === "function" ? sanitizeRealtimeUsage(msg.response?.usage) : null,
          status: msg.response?.status || null,
          provider_session_id: providerSession.id || null,
          provider_model: providerSession.model || null,
          model: caos?.diagnostics?.model || null,
          provider: "openai",
          transport: caos?.transport || "webrtc",
        },
      });
      // Fallback for a greeting that completed with no audio at all - see
      // createGreetingResponseGate's docstring in realtimeAutoResponseGate.js.
      greetingGate.onResponseDone();
      endCtl.noteResponseDone(msg.response);
      hangup.onResponseDone();
      farewellWatch.onResponseDone(msg.response);
      claimGuard.onResponseDone(msg.response);
      deltaLead.onResponseDone(msg.response);
      claimInterrupter.onResponseDone(msg.response);
    }
    if (msg.type === "response.created" && typedTurnRef?.current) onTypedResponseCreated(typedTurnRef.current);
    if (msg.type === "response.created") {
      endCtl.noteResponseCreated(msg.response?.id);
      hangup.onResponseCreated();
      claimInterrupter.onResponseCreated(msg.response?.id);
      logRealtimeEvent(sessionIdRef.current, "response_created", { responseId: msg.response?.id });
    }
    const typed = typedTurnFromMessage(msg);
    if (typed && !typedItemsSeen.has(typed.id)) {
      typedItemsSeen.add(typed.id);
      if (typedTurnRef?.current) onTypedEcho(typedTurnRef.current);
      setTranscript((t) => [...t, { role: "user", text: typed.text, ts: Date.now(), typed: true }]);
      turnGrounding.onTranscriptionCompleted(TYPED_TURN_CLASS, typed.text);
      postTurn("user", typed.text, typed.id, true);
      logRealtimeEvent(sessionIdRef.current, "user_transcript", { text: typed.text, meta: { typed: true } });
    }
    if (msg.type === "conversation.item.input_audio_transcription.completed") {
      if (typeof logTranscriptionUsage === "function") logTranscriptionUsage(sessionIdRef.current, msg, TRANSCRIPTION_MODEL, caos?.transport || "webrtc");   // paid usage receipt, no text
      const userText = msg.transcript || "";
      setTranscript((t) => [...t, { role: "user", text: userText, ts: Date.now() }]);
      // logprobs -> confidence, when the API actually returns them (it
      // currently doesn't over this path - Room 304 showed 0/34 turns with
      // a non-null value - so this safely no-ops until that changes).
      const confidence = transcriptionConfidence(msg.logprobs);
      const lowConfidence = confidence !== null && confidence < LOW_CONFIDENCE_THRESHOLD;
      const overlapped = turnSuspectRef.current === true || lowConfidence;
      const cls = classifyUserTurn({ overlapped, text: userText, lastAssistantText, tinyStreak: tinyFragmentStreak });
      tinyFragmentStreak = (cls.reason === "echo_like" || cls.reason === "uncertain_fragment" || cls.reason === "repeated_tiny_fragments") ? tinyFragmentStreak + 1 : 0;
      const heardEnd = classifyEndIntent(userText);
      // Room 214 rt_4ze7jvrt (2026-10-22 02:33): the server cut Michael's turn while the mic still carried his
      // voice and the cut came back "go away". An ending heard while the speaker is still talking is not their
      // goodbye: no local close, and the model's end_call on it gets the usual "did you want me to end...?".
      const stillTalking = heardEnd === "explicit" && micVoice?.stillTalking(lastSpeechStoppedAt) === true;
      // Ground for tool grounding (TSB-001) - NOT turnSuspectRef, see realtimeTurnGrounding.js.
      turnGrounding.onTranscriptionCompleted(stillTalking ? { ...cls, suspect: true, reason: "ending_while_talking" } : cls, userText);
      // 2026-08-22 (real bug, confirmed live): saved IMMEDIATELY now, not
      // stashed in a scalar "pending" ref to wait for the assistant reply.
      // A real ~15-second resident correction was silently lost when a
      // second, quicker segment overwrote that ref before the first was
      // ever persisted. Every real turn is now durably saved the moment
      // it's known, independent of whatever arrives next.
      postTurn("user", userText, msg.item_id, !cls.suspect);
      const sentenceGoesOn = heardEnd === "explicit" && isSoftEnding(userText) && sentenceTail.continues(msg.item_id);
      // Room 214 rt_qppzkpzp (2026-10-21 23:22): a ~0.2 s sound under Aria's speech came back as "That's all"
      // (uncertain_fragment) and hung up. A soft ending heard only as an uncertain overlap is not a goodbye;
      // "goodbye", "bye", "goodnight", "hang up" still end the call from the same kind of turn.
      const overlapHeld = heardEnd === "explicit" && !sentenceGoesOn && cls.reason === "uncertain_fragment" && isSoftEnding(userText);
      const endIntent = sentenceGoesOn || overlapHeld || stillTalking ? "none" : heardEnd;
      logRealtimeEvent(sessionIdRef.current, "user_transcript", {
        text: userText, assistantSpeaking: cls.suspect,
        meta: {
          confidence, low_confidence: lowConfidence, turn_class_reason: cls.reason,
          speech_segment_ms: lastSpeechSegmentMs,
          ms_since_assistant_stopped: lastPlaybackStoppedAt ? Date.now() - lastPlaybackStoppedAt : null,
          end_intent: endIntent, ...(sentenceGoesOn ? { soft_ending_continues_sentence: true } : {}),
          ...(overlapHeld ? { soft_ending_overlap_held: true } : {}),
          ...(stillTalking ? { ending_held_still_talking: true } : {}),
        },
      });
      // RQ-055: a clear spoken ending ends the call here, without asking the
      // model to choose a tool. Echo of Aria's own words never ends it.
      if (endIntent === "explicit" && turnMayEnd(cls.reason) && endCtl.beginEnding("transcript", { turn_class_reason: cls.reason })) {
        endCallKind = "resident_end_call";
        endVia = "transcript";
        farewellWatch.noteEndCallOk();   // the goodbye that follows is intended
        hangup.arm();   // closes after the goodbye audio finishes (endCallHangup.js)
      }
    }
    // FIXED 2026-08-09 (real, confirmed bug): the current Realtime API
    // emits this as response.output_audio_transcript.done, not
    // response.audio_transcript.done. Under the old name, Aria's own
    // spoken responses never landed in transcript state at all - only
    // user turns did, which is exactly why a real screenshot showed
    // every transcript line labeled "you" (there simply were no
    // "assistant" entries being added). Found via a full real WebRTC
    // connection test that logged every actual event type/name OpenAI
    // sent, not by guessing.
    if (msg.type === "response.output_audio_transcript.delta") { deltaLead.onDelta(msg); claimInterrupter.onTranscriptDelta(msg); }
    if (msg.type === "response.output_audio_transcript.done" && endCtl.wasCut(msg.response_id)) {
      // Cut off by the resident's goodbye: diagnostics only, never her turn (endController.js).
      logRealtimeEvent(sessionIdRef.current, "assistant_transcript_cut_by_goodbye", {
        text: msg.transcript || "", responseId: msg.response_id,
      });
    } else if (msg.type === "response.output_audio_transcript.done") {
      const aiText = msg.transcript || "";
      setTranscript((t) => [...t, { role: "assistant", text: aiText, ts: Date.now() }]);
      logRealtimeEvent(sessionIdRef.current, "assistant_transcript", { text: aiText });
      farewellWatch.onAssistantTranscript(aiText);
      claimGuard.onAssistantTranscript(aiText);
      lastAssistantText = aiText; // for classifyUserTurn()'s echo-resemblance check
      // Saved immediately and independently - see the matching comment on
      // the user-transcript handler above for why pairing was removed.
      postTurn("assistant", aiText, msg.item_id);
    }
    // Tool call dispatch — the OpenAI Realtime API streams arguments and
    // emits a single `done` event when the call is fully assembled.
    if (msg.type === "response.function_call_arguments.done") {
      handleFunctionCall({
        call_id: msg.call_id,
        name: msg.name,
        arguments: msg.arguments,
      });
    }
    if (msg.type === "error") {
      // A rejected session.update (bad config) must not leave the UI
      // reading "Live · idle" while a real error is showing - that's
      // exactly the misleading state a real acceptance test caught.
      // Downgrading status here doesn't end the call (audio may still
      // be flowing); it just stops the UI claiming full health.
      setError(msg.error?.message || "Realtime error");
      setStatus("error");
      // 2026-08-24: previously UI-only, invisible to any later DB query.
      logRealtimeEvent(sessionIdRef.current, "realtime_error", { text: msg.error?.message || "unknown" });
    }
  };

  return { handleFunctionCall, onMessage };
}
